import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@tectonic/errors';
import { Logger } from '@tectonic/logger';
import {
  requestOtpForUserUpdate,
  updateUser,
} from '@tectonic/remix-client-network';
import { delay } from '@tectonic/utils';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { useAuthStore } from '../../../store';
import { toPhoneText } from '../../../utils';

import type {
  ElemasonAuthProfileOtpConfirmationFormWidget,
  Nil,
  UserUpdateOtpRequestPayload,
} from '@tectonic/types';
import type { RefObject } from 'react';
import type {
  OtpConfirmationFormRef,
  ProfileEditFormValue,
} from '../../../components';

const requestOtpMutationFn = async (payload: UserUpdateOtpRequestPayload) => {
  const response = await requestOtpForUserUpdate(payload);
  if (response.error) {
    throw response.error;
  }
};

const updateProfileMutationFn = async (
  payload: Partial<ProfileEditFormValue> & { code: string }
) => {
  const phoneNumber = payload.phoneNumber!;
  const phone = toPhoneText(phoneNumber)!;
  const rPayload = {
    phone,
    code: payload.code,
    firstName: payload.firstName,
    lastName: payload.lastName,
  };
  const response = await updateUser(rPayload);

  if (response.error) {
    throw response.error;
  }
};

const useAuthProfileOtpConfirmationForm = (
  widget: ElemasonAuthProfileOtpConfirmationFormWidget,
  formRef: RefObject<Nil<OtpConfirmationFormRef>>
) => {
  const wData = widget.data!;

  const { errorMessages } = wData;
  const { showToast } = useToast();

  const profileUpdates = useAuthStore((state) => state.profileUpdates);

  const { isPending: isResendingOtp, mutateAsync: resendOtp } = useMutation({
    mutationKey: [],
    mutationFn: requestOtpMutationFn,
  });

  const { isPending: isSubmitting, mutateAsync: updateProfile } = useMutation({
    mutationFn: updateProfileMutationFn,
    mutationKey: [],
  });

  const onSubmit = async (code: string) => {
    try {
      const pUpdates = profileUpdates!;
      const payload = {
        code,
        phoneNumber: pUpdates.phoneNumber!,
        firstName: pUpdates.firstName,
        lastName: pUpdates.lastName,
      };
      await updateProfile(payload);
      showToast({ title: wData.messages.verifySuccess });
      await delay(1000);
      globalThis.location.reload();
    } catch (error) {
      Logger.error('useAuthProfileOtpConfirmationForm:onSubmit', error);
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  const onResendOtp = async () => {
    try {
      const phoneNumber = profileUpdates!.phoneNumber!;
      const phone = toPhoneText(phoneNumber)!;
      await resendOtp({ phone });
      showToast({ title: wData.messages.otpResendSuccess });
      formRef.current?.resetTimer();
    } catch (error) {
      Logger.error('useAuthProfileOtpConfirmationForm:onResendOtp', error);
      showToast({ title: getErrorMessage(error, errorMessages) });
    }
  };

  return { onSubmit, isResendingOtp, isSubmitting, onResendOtp };
};

export { useAuthProfileOtpConfirmationForm };
